<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div class="header-extra">
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('SEARCH_USER_GROUPS')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!loading"
      class="m-b-15"
    >
      <button
        type="button"
        class="btn btn-success btn-sm"
        :disabled="showAddNewForm"
        @click="displayNewForm"
      >
        <i class="fa fa-plus" />
        <span v-t="'USER_GROUPS_ADD_BUTTON'" />
      </button>
    </div>
    <v-table
      :data="userGroups"
      :current-page.sync="currentPage"
      :page-size="20"
      :hide-sort-icons="true"
      :filters="filters"
      class="table_beauty"
      @totalPagesChanged="totalPages = $event"
    >
      <thead slot="head">
        <tr>
          <th v-if="isMobile" />
          <v-th
            v-t="'USER_GROUP_TABLE_COL_NAME'"
            sort-key="name"
            default-sort="asc"
          />
          <v-th
            v-t="'USER_GROUP_TABLE_COL_TYPE'"
            sort-key="type"
          />
          <th
            v-t="'USER_GROUP_TABLE_COL_ADMIN_REPORT'"
            class="text-center"
            sort-key="adminReportView"
          />
          <th
            v-t="'USER_GROUP_TABLE_COL_READ_ONLY'"
            class="text-center"
            sort-key="readOnly"
          />
          <th
            v-if="!isMobile"
            v-t="'TABLE_COL_ACTIONS'"
          />
        </tr>
      </thead>
      <tbody
        slot="body"
        slot-scope="{ displayData }"
      >
        <tr
          v-if="showAddNewForm"
          class="row-inline-new-form slide-down bg-row-even"
        >
          <td colspan="100">
            <UserGroupForm
              v-if="!showAddNewInvites"
              :new-group="true"
              :show-loader="createGroupStatus.status === 'pending'"
              @cancel="hideNewForm"
              @submit="onCreate"
            />
            <div
              v-if="showAddNewInvites"
              class="p-l-15 p-r-15 p-t-15"
            >
              <legend class="f-s-13 p-b-10">
                Invite Users to <strong>{{ newGroup.name }}</strong>
                <button
                  type="button"
                  class="btn btn-xs btn-danger legend-right-button"
                  @click="closeNewGroupInvites"
                >
                  <i class="typcn typcn-delete" />
                  {{ createdNewInviteForNewGroup ? 'Close' : 'Skip' }}
                </button>
              </legend>
              <Invites
                invite-target-type="managers"
                :invite-target-id="newGroup.id"
                :existing-users="getExistingUsersForRole(newGroup.type)"
                @created-new-invite="inviteCreatedForNewGroup"
                @link-existing-user="linkExistingUser"
              />
            </div>
          </td>
        </tr>
        <template v-for="(row, index) in displayData">
          <tr
            v-if="!(isMobile && activeActionId == row.id)"
            :key="row.id"
            :class="{'bg-row-odd': index % 2 === 0, 'bg-row-even': index % 2 !== 0 }"
          >
            <td v-if="isMobile">
              <button
                class="btn btn-list-function btn-expand-actions"
                @click="displayMobileAction(row.id)"
              >
                <i class="fa fa-ellipsis-v" />
              </button>
            </td>
            <td
              class="clickable"
              @click="displayEditForm(row.id)"
            >
              {{ row.name }}
            </td>
            <td>{{ row.type === 'Affiliate' ? 'Publisher' : row.type }}</td>
            <td class="text-center">
              {{ row.adminReportView ? 'Yes' : 'No' }}
            </td>
            <td class="text-center">
              {{ row.readOnly ? 'Yes' : 'No' }}
            </td>
            <td v-if="!isMobile">
              <UserGroupActions
                :group="row"
                :disabled="formActive"
                :show-loader="updateGroupStatus.status === 'pending'"
                @show-edit="displayEditForm"
                @show-invites="displayInvites"
                @confirm-delete-group="confirmDeleteUserGroup"
              />
            </td>
          </tr>
          <!-- Actions Mobile collapsed view - Start -->
          <tr
            v-if="isMobile && activeActionId == row.id"
            class="mobile-actions ng-animate-disabled slide-right"
          >
            <td colspan="10">
              <UserGroupActions
                :group="row"
                :disabled="formActive"
                @collapse-actions="!formActive && hideMobileAction()"
                @show-edit="displayEditForm"
                @show-invites="displayInvites"
                @confirm-delete-group="confirmDeleteUserGroup"
              />
            </td>
          </tr>
          <!-- Actions Mobile collapsed view - End -->

          <tr
            v-if="showEditForm(row.id)"
            class="slide-down bg-row-even"
          >
            <td colspan="100">
              <UserGroupForm
                :new-group="false"
                :show-loader="updateGroupStatus.status === 'pending'"
                :group="row"
                @cancel="hideEditForm"
                @submit="updateGroup"
              />
            </td>
          </tr>

          <!-- Edit group invites row - START -->
          <tr
            v-if="showInviteForm(row.id)"
            class="slide-down bg-row-even"
          >
            <td colspan="100">
              <div class="p-l-15 p-r-15">
                <legend class="f-s-13 p-b-10">
                  Users
                  <button
                    type="button"
                    class="btn btn-xs btn-danger legend-right-button"
                    @click="hideInvites(row.id)"
                  >
                    <i class="typcn typcn-delete" />
                    Close
                  </button>
                </legend>
                <Invites
                  invite-target-type="managers"
                  :invite-target-id="row.id"
                  :existing-users="getExistingUsersForRole(row.type)"
                  @cancel="hideInvites"
                  @delete-user="deleteUser"
                  @link-existing-user="linkExistingUser"
                />
              </div>
            </td>
          </tr>
          <!-- Edit affiliate invites row - END -->
        </template>
      </tbody>
    </v-table>
    <div class="pagination-min">
      <div class="text-center p-7">
        <smart-pagination
          :current-page.sync="currentPage"
          :total-pages="totalPages"
          :max-page-links="10"
        />
      </div>
    </div>

    <div
      v-if="loading"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'USER_GROUPS_TABLE_LOADING'" />
    </div>
    <div
      v-if="emptyUserGroups"
      v-t="'USER_GROUPS_TABLE_EMPTY'"
      class="well well-sm text-center"
    />
    <!-- Delete Confirmation Modal Start -->
    <ConfirmationModal
      v-if="showModal"
      :entity="userGroupToDelete"
      :title="$t('DELETE_GROUP_MODAL_TITLE')"
      :cancel-button-text="$t('CANCEL')"
      :confirm-action-text="$t('DELETE')"
      :confirm-button-loading="deletingUserGroup"
      @confirm="deleteGroup"
      @close="hideModal"
    >
     <span v-html="$t('DELETE_GROUP_CONFIRMATION_MODAL_TEXT', { groupName: userGroupToDelete.name })">
        <br>
      </span>
    </ConfirmationModal>
    <!-- Delete Confirmation Modal End -->
  </PortalWidget>
</template>

<script>
import PortalWidget from '@/core/components/PortalWidget.vue';
import { isMobile } from '@/core/utilities';
import { mapActions, mapState, mapGetters } from 'vuex';
import messages from '../locales';
import Loader from '@/core/components/Loader';
import UserGroupForm from './UserGroupForm';
import UserGroupActions from './UserGroupActions';
import Invites from '@/modules/invites/components/invites';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import sharedMessages from '@/core/locales';
import ConfirmationModal from '@/core/components/ConfirmationModal/ConfirmationModal';

export default {
  name: 'UserGroups',
  components: {
    Loader,
    UserGroupForm,
    UserGroupActions,
    Invites,
    PortalWidget,
    ConfirmationModal
  },
  props: { },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      showAddNewForm: false,
      showAddNewInvites: false,
      createdNewInviteForNewGroup: false,
      showEditFormRowId: null,
      showInvitesRowId: null,
      showModal: false,
      userGroupToDelete: null,
      isMobile: isMobile,
      activeActionId: null,
      newGroup: null,
      widget: null,
      filters: {
        name: { value: '', keys: ['name'] }
      }
    };
  },
  computed: {
    ...mapState('userGroups', ['userGroups']),
    ...mapGetters('userGroups', [
      'getUserGroupsStatus',
      'createGroupStatus',
      'updateGroupStatus',
      'deleteGroupStatus',
      'unlinkUserStatus',
      'linkExistingUserStatus',
      'existingBuyerManagerUsers',
      'existingPubManagerUsers']),
    createAccess() { return true; },
    loading() {
      return this.getUserGroupsStatus.status === STATUS_PENDING;
    },
    emptyUserGroups() {
      return this.getUserGroupsStatus.status === STATUS_SUCCESS && this.userGroups && this.userGroups.length === 0;
    },
    formActive() {
      return this.showAddNewInvites || this.showAddNewForm || !!this.showInvitesRowId || !!this.showEditFormRowId;
    },
    deletingUserGroup() {
      return this.deleteGroupStatus.status === STATUS_PENDING;
    },
  },
  watch: {
    currentPage() {
      this.hideEditForm();
      this.hideInvites();
      this.closeNewGroupInvites();
    },
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.hideEditForm();
        this.hideInvites();
        this.closeNewGroupInvites();
      }
    }
  },
  created() {
    this.getUserGroups();
    this.getExistingManagerUsers();
  },
  beforeMount() {
    this.widget = {
      title: this.$i18n.t('USER_GROUPS_HEADER'),
      iconClass: 'fa fa-users'
    };
  },
  methods: {
    ...mapActions('userGroups', [
      'getUserGroups',
      'createUserGroup',
      'updateUserGroup',
      'deleteUserGroup',
      'unlinkUserFromGroup',
      'getExistingManagerUsers',
      'linkExistingUserToGroup']),
    confirmDeleteUserGroup(group) {
      this.userGroupToDelete = {
        id: group.id,
        name: group.name
      };
      this.showModal = true;
    },
    hideModal() {
      this.showModal = false;
      this.userGroupToDelete = null;
    },
    displayNewForm() {
      this.hideEditForm();
      this.showAddNewForm = true;
    },
    hideNewForm() {
      this.showAddNewForm = false;
    },
    displayEditForm(rowId) {
      this.hideNewForm();
      this.hideInvites();
      this.showEditFormRowId = rowId;
    },
    hideEditForm() {
      this.showEditFormRowId = null;
    },
    displayInvites(rowId) {
      this.showInvitesRowId = rowId;
    },
    hideInvites() {
      this.showInvitesRowId = null;
    },
    displayMobileAction(rowId) {
      this.activeActionId = rowId;
    },
    hideMobileAction() {
      this.activeActionId = null;
    },
    showEditForm(rowId) {
      return !this.showAddNewInvites && !this.showAddNewForm && !this.showInvitesRowId && this.showEditFormRowId == rowId;
    },
    showInviteForm(rowId) {
      return !this.showAddNewInvites && !this.showAddNewForm && !this.showEditFormRowId && this.showInvitesRowId == rowId;
    },
    closeNewGroupInvites() {
      this.showAddNewInvites = false;
      this.createdNewInviteForNewGroup = false;
      this.hideNewForm();
    },
    inviteCreatedForNewGroup() {
      this.createdNewInviteForNewGroup = true;
    },
    getExistingUsersForRole(groupType) {
      if (groupType === 'Affiliate') {
        return this.existingPubManagerUsers;
      }
      if (groupType === 'Buyer') {
        return this.existingBuyerManagerUsers;
      }
      return [];
    },
    async onCreate(group) {
      await this.createUserGroup(group);
      if (this.createGroupStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('CREATE_USER_GROUP_SUCCESS'));
        this.newGroup = this.createGroupStatus.newGroup;
        this.showAddNewInvites = true;
      }
      if (this.createGroupStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.createGroupStatus.error);
      }
    },
    async updateGroup(group) {
      await this.updateUserGroup(group);
      if (this.updateGroupStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('UPDATE_USER_GROUP_SUCCESS'));
        this.hideEditForm();
      }
      if (this.updateGroupStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.updateGroupStatus.error);
      }
    },
    async deleteGroup(group) {
      await this.deleteUserGroup(group.id);
      if (this.deleteGroupStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('DELETE_USER_GROUP_SUCCESS'));
      }
      if (this.deleteGroupStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.$i18n.t('DELETE_USER_GROUP_FAILURE'));
      }
      this.hideModal();
    },
    async deleteUser(userToDelete) {
      if (userToDelete?.inviteTargetId && userToDelete?.invite?.user?.id) {
        await this.unlinkUserFromGroup({
          managerId: userToDelete.inviteTargetId,
          userId: userToDelete.invite.user.id,
          managerCombinedIDs: userToDelete.invite.user.managerCombinedIDs
        });
        if (this.unlinkUserStatus.status === STATUS_SUCCESS) {
          utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('UNLINK_USER_SUCCESS'));
        }
        if (this.unlinkUserStatus.status === STATUS_ERROR) {
          utilities.showToaster(STATUS_ERROR, this.$i18n.t('UNLINK_USER_FAIL'));
        }
      } else {
        utilities.showToaster(STATUS_ERROR, this.$i18n.t('UNLINK_USER_FAIL'));
      }
    },
    async linkExistingUser(eu) {
      await this.linkExistingUserToGroup(eu);
      if (this.linkExistingUserStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('LINK_EXISTING_USER_SUCCESS'));
      }
      if (this.linkExistingUserStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.$i18n.t('LINK_EXISTING_USER_FAILURE'));
      }
    },
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>
